import { useEffect, useState, useCallback } from "react";
import { Network, Sparkles, CircleDot, Triangle, Square } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { SectionIntro } from "@/components/SectionIntro";
import { fetchCorrespondencesMap } from "@/lib/api";
import { pickText } from "@/lib/content";

const PILLAR_COLORS = {
  central: "#D4AF37",
  mercy: "#507687",
  severity: "#B33F36",
};

const ELEMENT_COLORS = {
  Spirit: "#F5F5FA",
  Light: "#F0E6D2",
  Water: "#507687",
  Ether: "#8FA3AD",
  Air: "#8FA3AD",
  Fire: "#B33F36",
  Earth: "#8C7352",
  "Earth/Water": "#6B6342",
};

const BODY_POSITIONS = {
  crown: { y: 4 },
  head: { y: 14 },
  throat: { y: 24 },
  chest: { y: 36 },
  heart: { y: 42 },
  stomach: { y: 54 },
  sacral: { y: 66 },
  root: { y: 78 },
  legs: { y: 88 },
};

const PILLAR_X = {
  mercy: 22,
  central: 50,
  severity: 78,
};

export const CorrespondencesPage = ({ language, vipEmail }) => {
  const [correspondences, setCorrespondences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [hoveredSystem, setHoveredSystem] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchCorrespondencesMap(vipEmail);
      setCorrespondences(data);
    } catch {
      toast.error("Failed to load correspondences map.");
    } finally {
      setLoading(false);
    }
  }, [vipEmail]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isHighlighted = (c) => {
    if (!hoveredSystem) return true;
    if (hoveredSystem.type === "sefira") return c.sefira === hoveredSystem.id;
    if (hoveredSystem.type === "chakra") return c.chakra === hoveredSystem.id;
    if (hoveredSystem.type === "tattva") return c.tattva === hoveredSystem.id;
    return true;
  };

  const uniqueSystems = {
    sefirot: [...new Set(correspondences.filter((c) => c.sefira).map((c) => ({ id: c.sefira, title: c.sefira_title })))].filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
    ),
    chakras: [...new Set(correspondences.filter((c) => c.chakra).map((c) => ({ id: c.chakra, title: c.chakra_title })))].filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
    ),
    tattvas: [...new Set(correspondences.filter((c) => c.tattva).map((c) => ({ id: c.tattva, title: c.tattva_title })))].filter(
      (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
    ),
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="correspondences-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="correspondences-page">
      <SectionIntro
        eyebrow={pickText(language, "Sacred Geometry", "Geometria Sacra")}
        title={pickText(language, "The Correspondences Map", "La Mappa delle Corrispondenze")}
        description={pickText(
          language,
          "Trace the hidden threads connecting Sephiroth, Chakras, and Tattvas across the body-temple. Click any node to reveal its connections.",
          "Traccia i fili nascosti che collegano Sefirot, Chakra e Tattva attraverso il corpo-tempio. Clicca su un nodo per rivelare le sue connessioni.",
        )}
        testId="correspondences-intro"
      />

      <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
        {/* System Filter Sidebar */}
        <div className="space-y-6" data-testid="correspondences-sidebar">
          <SystemPanel
            title={pickText(language, "Sefirot", "Sefirot")}
            items={uniqueSystems.sefirot}
            type="sefira"
            icon={<Sparkles className="h-4 w-4" />}
            color="#D4AF37"
            hoveredSystem={hoveredSystem}
            onHover={setHoveredSystem}
          />
          <SystemPanel
            title={pickText(language, "Chakras", "Chakra")}
            items={uniqueSystems.chakras}
            type="chakra"
            icon={<CircleDot className="h-4 w-4" />}
            color="#507687"
            hoveredSystem={hoveredSystem}
            onHover={setHoveredSystem}
          />
          <SystemPanel
            title={pickText(language, "Tattvas", "Tattva")}
            items={uniqueSystems.tattvas}
            type="tattva"
            icon={<Triangle className="h-4 w-4" />}
            color="#B33F36"
            hoveredSystem={hoveredSystem}
            onHover={setHoveredSystem}
          />
        </div>

        {/* Main Visual Map */}
        <div className="glass-card rounded-[2rem] p-6 md:p-8" data-testid="correspondences-visual-map">
          <svg viewBox="0 0 100 100" className="h-auto w-full" style={{ minHeight: "500px" }}>
            {/* Pillar lines */}
            <line x1="22" y1="2" x2="22" y2="95" stroke="#507687" strokeWidth="0.3" strokeDasharray="1,1" opacity="0.4" />
            <line x1="50" y1="2" x2="50" y2="95" stroke="#D4AF37" strokeWidth="0.3" strokeDasharray="1,1" opacity="0.4" />
            <line x1="78" y1="2" x2="78" y2="95" stroke="#B33F36" strokeWidth="0.3" strokeDasharray="1,1" opacity="0.4" />

            {/* Pillar labels */}
            <text x="22" y="98" textAnchor="middle" fill="#507687" fontSize="2.2" fontFamily="Manrope" opacity="0.6">
              {pickText(language, "Mercy", "Misericordia")}
            </text>
            <text x="50" y="98" textAnchor="middle" fill="#D4AF37" fontSize="2.2" fontFamily="Manrope" opacity="0.6">
              {pickText(language, "Central", "Centrale")}
            </text>
            <text x="78" y="98" textAnchor="middle" fill="#B33F36" fontSize="2.2" fontFamily="Manrope" opacity="0.6">
              {pickText(language, "Severity", "Severita")}
            </text>

            {/* Connection lines between related nodes */}
            {correspondences.map((c, i) => {
              const pos = BODY_POSITIONS[c.body_region] || { y: 50 };
              const x = PILLAR_X[c.pillar] || 50;
              const opacity = isHighlighted(c) ? 0.6 : 0.08;
              // Draw lines to neighbors
              return correspondences.slice(i + 1).map((c2) => {
                if (
                  (c.sefira && c2.sefira) ||
                  (c.chakra && c2.chakra) ||
                  (c.body_region === c2.body_region)
                ) {
                  const pos2 = BODY_POSITIONS[c2.body_region] || { y: 50 };
                  const x2 = PILLAR_X[c2.pillar] || 50;
                  return (
                    <line
                      key={`line-${c.id}-${c2.id}`}
                      x1={x}
                      y1={pos.y}
                      x2={x2}
                      y2={pos2.y}
                      stroke={ELEMENT_COLORS[c.element] || "#D4AF37"}
                      strokeWidth="0.15"
                      opacity={opacity}
                      style={{ transition: "opacity 0.5s ease" }}
                    />
                  );
                }
                return null;
              });
            })}

            {/* Nodes */}
            {correspondences.map((c) => {
              const pos = BODY_POSITIONS[c.body_region] || { y: 50 };
              const x = PILLAR_X[c.pillar] || 50;
              const color = PILLAR_COLORS[c.pillar] || "#D4AF37";
              const highlighted = isHighlighted(c);
              const isSelected = selected?.id === c.id;
              const nodeRadius = isSelected ? 2.8 : 2;

              return (
                <g
                  key={c.id}
                  onClick={() => setSelected(isSelected ? null : c)}
                  style={{ cursor: "pointer", transition: "opacity 0.5s ease" }}
                  opacity={highlighted ? 1 : 0.2}
                  data-testid={`correspondence-node-${c.id}`}
                >
                  {/* Glow */}
                  <circle cx={x} cy={pos.y} r={nodeRadius + 1.5} fill={color} opacity={isSelected ? 0.2 : 0.05} />
                  {/* Node */}
                  <circle
                    cx={x}
                    cy={pos.y}
                    r={nodeRadius}
                    fill="#0A0A12"
                    stroke={color}
                    strokeWidth={isSelected ? 0.6 : 0.35}
                  />
                  {/* Inner dot */}
                  <circle cx={x} cy={pos.y} r={0.8} fill={ELEMENT_COLORS[c.element] || color} opacity={0.9} />

                  {/* Label */}
                  <text
                    x={c.pillar === "mercy" ? x - 4 : c.pillar === "severity" ? x + 4 : x}
                    y={pos.y - 3.5}
                    textAnchor="middle"
                    fill={color}
                    fontSize="1.8"
                    fontFamily="Manrope"
                    opacity={highlighted ? 0.9 : 0.3}
                  >
                    {c.sefira_title || c.chakra_title || c.tattva_title || ""}
                  </text>

                  {/* Second system label */}
                  {c.sefira_title && c.chakra_title && (
                    <text
                      x={c.pillar === "mercy" ? x - 4 : c.pillar === "severity" ? x + 4 : x}
                      y={pos.y + 4.5}
                      textAnchor="middle"
                      fill="#507687"
                      fontSize="1.5"
                      fontFamily="Manrope"
                      opacity={highlighted ? 0.7 : 0.2}
                    >
                      {c.chakra_title}
                    </text>
                  )}
                  {c.tattva_title && (
                    <text
                      x={c.pillar === "mercy" ? x - 4 : c.pillar === "severity" ? x + 4 : x}
                      y={pos.y + (c.sefira_title && c.chakra_title ? 7 : 4.5)}
                      textAnchor="middle"
                      fill="#B33F36"
                      fontSize="1.5"
                      fontFamily="Manrope"
                      opacity={highlighted ? 0.6 : 0.2}
                    >
                      {c.tattva_title}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Selected Detail Card */}
      {selected && (
        <div
          className="glass-card grain-mask animate-in fade-in slide-in-from-bottom-4 rounded-[2rem] border-primary/20 p-6 md:p-8"
          data-testid="correspondence-detail-card"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="section-label" data-testid="correspondence-detail-region">
                {selected.body_region.toUpperCase()} — {selected.element}
              </p>
              <h3 className="mt-2 text-3xl text-foreground" data-testid="correspondence-detail-title">
                {[selected.sefira_title, selected.chakra_title, selected.tattva_title].filter(Boolean).join(" + ")}
              </h3>
            </div>
            <button
              className="rounded-full border border-white/10 p-2 text-slate-400 transition-colors hover:border-primary/30 hover:text-primary"
              onClick={() => setSelected(null)}
              data-testid="correspondence-detail-close"
            >
              <Square className="h-4 w-4" />
            </button>
          </div>
          <p className="body-copy mt-4 max-w-3xl" data-testid="correspondence-detail-description">
            {pickText(language, selected.description_en, selected.description_it)}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {selected.sefira_title && (
              <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#D4AF37]">
                Sefira: {selected.sefira_title}
              </span>
            )}
            {selected.chakra_title && (
              <span className="rounded-full border border-[#507687]/30 bg-[#507687]/10 px-3 py-1 text-xs text-[#507687]">
                Chakra: {selected.chakra_title}
              </span>
            )}
            {selected.tattva_title && (
              <span className="rounded-full border border-[#B33F36]/30 bg-[#B33F36]/10 px-3 py-1 text-xs text-[#B33F36]">
                Tattva: {selected.tattva_title}
              </span>
            )}
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
              {pickText(language, `Pillar: ${selected.pillar}`, `Colonna: ${selected.pillar}`)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

function SystemPanel({ title, items, type, icon, color, hoveredSystem, onHover }) {
  return (
    <div className="glass-card rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3" style={{ color }}>
        {icon}
        <p className="text-xs font-semibold uppercase tracking-[0.2em]">{title}</p>
      </div>
      <div className="space-y-1">
        {items.map((item) => {
          const isActive = hoveredSystem?.type === type && hoveredSystem?.id === item.id;
          return (
            <button
              key={item.id}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-all duration-300 ${
                isActive
                  ? "border border-white/20 bg-white/10 text-foreground"
                  : "border border-transparent text-slate-400 hover:bg-white/5 hover:text-slate-200"
              }`}
              onMouseEnter={() => onHover({ type, id: item.id })}
              onMouseLeave={() => onHover(null)}
              onClick={() => onHover(isActive ? null : { type, id: item.id })}
              data-testid={`system-filter-${type}-${item.id}`}
            >
              {item.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}
